import { defineStore } from 'pinia'
import { computed, ref, shallowRef, watch } from 'vue'
import { LocationQuery, useRoute, useRouter } from 'vue-router'

import { DEFAULT_CLIDS } from '@/constants/general'
import { SelectedExperimentsData } from '@/server/experiments/types'
import { ExportMode, type PlatformType } from '@/types'
import { processLandingAttributes } from '@/utils/affiliate'
import { idleCallbackPromise } from '@/utils/helpers'
import safeURLParse from '@/utils/safeURLParse'

export type ErrorStatus = 404 | 500
export type PageStatus = 200 | 301 | 302 | ErrorStatus

const DEFAULT_PLATFORM = 'desktop'
const WIDGET_ID_PARAM_NAME = 'widget_id'
const CLID_PARAM = 'clid'
const EXPORT_MODE_PARAM = 'export_mode'
const GAME_DISTRIBUTION_KEY = 'use_gd_ads'

export const useAppStore = defineStore('app', () => {
    const pageStatus = ref<PageStatus>(200)
    const targetURL = ref<string>('')
    const platform = ref<PlatformType>(DEFAULT_PLATFORM)
    const host = ref<string>()
    const referer = ref<string>()
    const isIframe = ref<boolean>()
    const userAcquisitionParams = ref<Record<string, string>>({})
    const query = ref<LocationQuery>()
    const experiments = shallowRef<SelectedExperimentsData | undefined>()
    const experimentsString = computed(() => {
        return experiments.value
            ? Object.keys(experiments.value)
                  .map((key) => `${key}:${experiments.value![key].sample}`)
                  .join(', ')
            : ''
    })
    const isGameDistribution = computed(() => query.value?.[GAME_DISTRIBUTION_KEY])
    const isDebugMode = computed(() => query.value?.debug === 'true')
    const headers = ref<Record<string, string>>({})

    const route = useRoute()
    const { landingClid, landingWidgetId } = processLandingAttributes(route)

    const widgetId = computed(() => {
        return (query.value?.[WIDGET_ID_PARAM_NAME] || landingWidgetId) as string
    })
    const clid = computed(() => {
        const parentHost = (isIframe.value && safeURLParse(referer.value || '')?.hostname) || host.value
        let fallbackClid = DEFAULT_CLIDS.unknown

        if (parentHost === 'playgama.com') {
            fallbackClid = DEFAULT_CLIDS.direct
        }

        if (parentHost?.endsWith('.playgama.com')) {
            fallbackClid = DEFAULT_CLIDS.directTest
        }

        return (query.value?.[CLID_PARAM] as string) || landingClid || fallbackClid
    })

    const exportMode = computed((): ExportMode => {
        switch (query.value?.[EXPORT_MODE_PARAM]) {
            case 'portal':
                return 'portal'
            case 'widget':
                return 'widget'
        }
        return 'unknown'
    })

    const isPortal = computed(() => exportMode.value === 'portal')
    const isWidget = computed(() => exportMode.value === 'widget')

    const queryParams = computed(() => Object.entries(query.value || {}))

    const isAuthBannerOpen = ref<boolean>(false)

    function setHeaders(value: Record<string, string>) {
        headers.value = value
    }

    function setPageStatus(status: PageStatus) {
        pageStatus.value = status
    }
    function setUserParams(details: { userAcquisitionParams: Record<string, string> }) {
        userAcquisitionParams.value = details.userAcquisitionParams
    }

    function setTargetURL(target: string) {
        targetURL.value = target
    }

    function setPlatform(plt: PlatformType) {
        platform.value = plt
    }

    function setUrlDetails(details: { query: LocationQuery; host: string; referer?: string; isIframe?: boolean }) {
        host.value = details.host
        query.value = details.query
        referer.value = details.referer
        isIframe.value = details.isIframe
    }

    function setExperiments(samples?: SelectedExperimentsData) {
        experiments.value = samples
    }

    function $reset() {
        pageStatus.value = 200
        targetURL.value = ''
        platform.value = DEFAULT_PLATFORM
    }

    // TODO remove this, and targetURL, setTargetURL, setPageStatus all together
    // use router methods instead
    const router = useRouter()
    watch(
        () => [pageStatus.value, targetURL.value],
        () => {
            if (targetURL.value !== '' && pageStatus.value > 300 && pageStatus.value < 400) {
                router.replace(targetURL.value)
                pageStatus.value = 200
                targetURL.value = ''
            }
        },
    )

    const pageLoadProgress = ref(false)
    // this is called in InitWrapper after initial navigation is done
    // so here we track all but first navigation, which is exactly what we want
    function initPageLoadProgressTracking() {
        router.beforeEach(async () => {
            pageLoadProgress.value = true
            // unblock thread
            await idleCallbackPromise()
        })
        router.afterEach(() => {
            pageLoadProgress.value = false
        })
    }

    return {
        setPageStatus,
        setTargetURL,
        setUrlDetails,
        setHeaders,
        initPageLoadProgressTracking,
        headers,
        pageStatus,
        targetURL,
        userAcquisitionParams,
        clid,
        host,
        referer,
        isIframe,
        queryParams,
        query,
        widgetId,
        platform,
        exportMode,
        isAuthBannerOpen,
        isGameDistribution,
        isDebugMode,
        isWidget,
        isPortal,
        setPlatform,
        setUserParams,
        setExperiments,
        $reset,
        experiments,
        experimentsString,
        pageLoadProgress,
    }
})
